/* ============================================================
   Fetch wrapper around the Worker endpoints.
   ------------------------------------------------------------
   `credentials: "include"` on every call so the session cookie
   (`st_session`, set by the Worker at OAuth callback time) is
   attached to cross-origin requests from the Pages frontend.
   The Worker sets the cookie with SameSite=None + Secure to
   permit that cross-origin attach.
   ============================================================ */

// Same-origin: the Pages Function at /api/[[path]].js proxies to the Worker.
// This makes the session cookie first-party (browsers block third-party
// cookies even with SameSite=None + Secure). See functions/api/[[path]].js
// for the proxy code.
const API_BASE = import.meta.env.VITE_API_BASE ?? "/api";

async function j(url, opts) {
  const res = await fetch(url, {
    credentials: "include",
    ...opts,
    headers: { "Content-Type": "application/json", ...(opts && opts.headers || {}) },
  });
  if (!res.ok) {
    let body = null;
    try { body = await res.json(); } catch {}
    const err = new Error(`http_${res.status}`);
    err.status = res.status;
    err.body = body; // structured server errors surface here (e.g. { error: "bad_credentials" })
    throw err;
  }
  return res.json();
}

export const api = {
  /* ---------- Auth (email + password, invite-gated) ---------- */
  auth: {
    // Returns { status: 'invited_new' | 'existing' | 'not_invited' }.
    // Drives Login.jsx's three-state UI (email → register OR login OR reject).
    checkEmail: (email)   => j(`${API_BASE}/auth/check-email`, { method: "POST", body: JSON.stringify({ email }) }),
    register:   (payload) => j(`${API_BASE}/auth/register`,    { method: "POST", body: JSON.stringify(payload) }),
    login:      (payload) => j(`${API_BASE}/auth/login`,       { method: "POST", body: JSON.stringify(payload) }),
    whoami:     ()        => j(`${API_BASE}/auth/whoami`),
    logout:     ()        => j(`${API_BASE}/auth/logout`,      { method: "POST" }),
  },

  /* ---------- Vendor data (session-gated on the Worker) ---------- */
  quotes:    (symbols) => j(`${API_BASE}/quotes?symbols=${encodeURIComponent(symbols.join(","))}`),
  news:      (symbol)  => j(`${API_BASE}/news?symbol=${encodeURIComponent(symbol)}`),
  movers:    (sector)  => j(`${API_BASE}/movers?sector=${encodeURIComponent(sector)}`),
  // detail returns all four timeframes pre-loaded — the tf toggle switches client-side, no re-fetch.
  detail:    (symbol)  => j(`${API_BASE}/detail?symbol=${encodeURIComponent(symbol)}`),
  // chart candles — range switch triggers a fresh fetch (each range is a
  // different vendor call). 60s server-side cache absorbs rapid re-clicks.
  candles:   (symbol, range) =>
    j(`${API_BASE}/candles?symbol=${encodeURIComponent(symbol)}&range=${encodeURIComponent(range)}`),
  profile:   (symbol)  => j(`${API_BASE}/profile?symbol=${encodeURIComponent(symbol)}`),
  profiles:  (symbols) => j(`${API_BASE}/profiles?symbols=${encodeURIComponent(symbols.join(","))}`),
  tabs:      ()        => j(`${API_BASE}/tabs`),

  /* ---------- Watchlist (per-user, gated) ---------- */
  watchlist: {
    // MainPage uses .full() — one round trip returns sym+name+price+chg+lean+tags for
    // every row in the tab. .list() / .leans() kept for admin/debug callers only.
    full:    (tab)             => j(`${API_BASE}/watchlist/full?tab=${encodeURIComponent(tab)}`),
    list:    (tab)             => j(`${API_BASE}/watchlist?tab=${encodeURIComponent(tab)}`),
    leans:   (tab)             => j(`${API_BASE}/watchlist/leans?tab=${encodeURIComponent(tab)}`),
    add:     (payload)         => j(`${API_BASE}/watchlist`,       { method: "POST",   body: JSON.stringify(payload) }),
    setTags: (stockId, tags)   => j(`${API_BASE}/watchlist/tags`,  { method: "POST",   body: JSON.stringify({ stockId, tags }) }),
    remove:  (id)              => j(`${API_BASE}/watchlist/${id}`, { method: "DELETE" }),
  },
};
